import { Macro, MacroContext, MacroEstimate, MacroSafety, MacroStatus, BaseMacro } from './Macro';
import { CirculariseMacro, DeorbitMacro, DockingMacro, HoverMacro, LandingMacro } from './FlightMacros';
import { LaunchToOrbitMacro } from './LaunchToOrbit';
import { TransferToTargetMacro } from './TransferToTarget';

export type MacroId =
  | 'launchToOrbit'
  | 'circularise'
  | 'deorbit'
  | 'land'
  | 'hover'
  | 'dock'
  | 'transferToTarget';

export interface MacroProgress {
  id: string;
  name: string;
  status: MacroStatus;
  phase: string;
  phaseIndex: number;
  phaseCount: number;
  statusText: string;
  elapsed: number;
  propellantUsed: number;
}

/**
 * Owns the active macro and mediates between it and the player.
 *
 * Three rules define the relationship:
 *
 *  1. The player can always pause, cancel, or simply take the stick. Any
 *     meaningful manual input pauses the macro rather than fighting it.
 *  2. A macro reports what it intends to do before it starts, using estimates
 *     derived from the vessel's real state.
 *  3. A macro can fail. When it does it says why and hands the vessel back in a
 *     defined configuration, rather than leaving hold modes latched.
 */
export class MacroRunner {
  private active: Macro | null = null;
  private startTime = 0;
  private startFuel = 0;
  private pausedByPilot = false;

  /** Stick deflection above which manual input pauses a running macro. */
  overrideThreshold = 0.15;

  readonly safety: MacroSafety = { margin: 0.35, preferEfficiency: false };

  /** Last completed or failed macro's closing message. */
  lastMessage: string | null = null;

  create(id: MacroId, options: { targetAltitude?: number; targetPeriapsis?: number } = {}): Macro {
    switch (id) {
      case 'launchToOrbit':
        return new LaunchToOrbitMacro(options.targetAltitude ?? 200000);
      case 'circularise':
        return new CirculariseMacro();
      case 'deorbit': {
        const m = new DeorbitMacro();
        if (options.targetPeriapsis !== undefined) m.targetPeriapsis = options.targetPeriapsis;
        return m;
      }
      case 'land':
        return new LandingMacro();
      case 'hover':
        return new HoverMacro();
      case 'dock':
        return new DockingMacro();
      case 'transferToTarget':
        return new TransferToTargetMacro();
      default:
        throw new Error(`Unknown macro "${id}"`);
    }
  }

  /** Estimates a macro without starting it, for the confirmation panel. */
  preview(id: MacroId, ctx: MacroContext, options: Parameters<MacroRunner['create']>[1] = {}): MacroEstimate {
    const macro = this.create(id, options);
    if (macro instanceof BaseMacro) macro.safety = { ...this.safety };
    return macro.estimate(ctx);
  }

  start(macro: Macro, ctx: MacroContext): boolean {
    if (this.active && this.active.status === 'running') {
      this.cancel(ctx);
    }
    if (macro instanceof BaseMacro) macro.safety = { ...this.safety };
    macro.start(ctx);
    if (macro.status === 'failed') {
      this.lastMessage = `${macro.name} could not start: ${macro.statusText}`;
      this.active = null;
      return false;
    }
    this.active = macro;
    this.startTime = ctx.t;
    this.startFuel = ctx.ship.massProperties.fuel;
    this.pausedByPilot = false;
    this.lastMessage = null;
    return true;
  }

  startById(id: MacroId, ctx: MacroContext, options: Parameters<MacroRunner['create']>[1] = {}): boolean {
    return this.start(this.create(id, options), ctx);
  }

  pause(): void {
    if (this.active && this.active.status === 'running') {
      (this.active as BaseMacro).status = 'paused';
    }
  }

  resume(): void {
    if (this.active && this.active.status === 'paused') {
      (this.active as BaseMacro).status = 'running';
      this.pausedByPilot = false;
    }
  }

  cancel(ctx: MacroContext): void {
    if (!this.active) return;
    this.active.abort(ctx);
    this.lastMessage = `${this.active.name} cancelled`;
    this.active = null;
  }

  get current(): Macro | null {
    return this.active;
  }

  get isRunning(): boolean {
    return this.active !== null && this.active.status === 'running';
  }

  /**
   * Advances the active macro. `pilotInputMagnitude` is the largest absolute
   * stick or throttle deflection this step; anything above the override
   * threshold pauses the macro so manual control takes precedence immediately.
   */
  update(ctx: MacroContext, pilotInputMagnitude: number): void {
    if (!this.active) return;

    if (pilotInputMagnitude > this.overrideThreshold && this.active.status === 'running') {
      (this.active as BaseMacro).status = 'paused';
      (this.active as BaseMacro).statusText = 'Paused: manual control input detected';
      this.pausedByPilot = true;
      return;
    }

    if (this.active.status === 'paused') {
      if (this.pausedByPilot && pilotInputMagnitude <= this.overrideThreshold * 0.5) {
        // Do not silently resume: the player has taken control and must say so.
      }
      return;
    }

    const status = this.active.update(ctx);
    if (status === 'complete' || status === 'failed' || status === 'aborted') {
      this.lastMessage =
        status === 'complete'
          ? `${this.active.name}: ${this.active.statusText || 'complete'}`
          : `${this.active.name} ${status}: ${this.active.statusText}`;
      this.active = null;
    }
  }

  progress(ctx: MacroContext): MacroProgress | null {
    const m = this.active;
    if (!m) return null;
    return {
      id: m.id,
      name: m.name,
      status: m.status,
      phase: m.phases[Math.min(m.phaseIndex, m.phases.length - 1)],
      phaseIndex: m.phaseIndex,
      phaseCount: m.phases.length,
      statusText: m.statusText,
      elapsed: ctx.t - this.startTime,
      propellantUsed: Math.max(0, this.startFuel - ctx.ship.massProperties.fuel),
    };
  }
}
